import { useEffect } from 'react'
import type { QueryClient } from '@tanstack/react-query'
import { nativeQueueManager } from './nativeQueueManager'

/** Stage read query; its `project.ownerLicenceStatus` decides about writes on the stage (W1). */
const STAGE_QUERY_ROOT = 'currStageUsersQuery'

const WRITABLE_STATUSES = new Set(['active', 'expiring'])

export function readOwnerLicenceStatus(data: unknown): string | null {
  if (!data || typeof data !== 'object') return null
  const project = (data as { project?: unknown }).project
  if (!project || typeof project !== 'object') return null
  const status = (project as { ownerLicenceStatus?: unknown }).ownerLicenceStatus
  return typeof status === 'string' ? status : null
}

/**
 * Replays licence-held queue groups when a stage read shows its project owner's licence turn
 * valid again (anything → `active`/`expiring`).
 *
 * The owner's status, not `/users/me`: under W1 the licence of whoever owns the data refuses the
 * write, and a partner company working on an expired site has a valid licence of its own (or
 * none). Queue rows do not record their project, so any stage turning writable replays every
 * held group; a group whose own licence is still expired just answers 403 again, which costs a
 * request and no retry budget. The hourly replay inside the queue covers stages nobody opens.
 */
export function watchOwnerLicenceRenewal(queryClient: QueryClient): () => void {
  const lastStatusByQuery = new Map<string, string | null>()
  // Seed from what is already cached (restored from IndexedDB, or read before this mounted):
  // otherwise the first refetch after an offline cold start — the one that sees the renewal —
  // would only establish the baseline and the transition would go unnoticed.
  for (const query of queryClient.getQueryCache().findAll({ queryKey: [STAGE_QUERY_ROOT] })) {
    if (query.state.data !== undefined) {
      lastStatusByQuery.set(query.queryHash, readOwnerLicenceStatus(query.state.data))
    }
  }

  return queryClient.getQueryCache().subscribe(event => {
    const key = event.query.queryKey
    if (!Array.isArray(key) || key[0] !== STAGE_QUERY_ROOT) return
    // A removed stage (organisation switch, logout) is uncached again: its next read is a first
    // observation, not a comparison with a status that may be months out of date.
    if (event.type === 'removed') {
      lastStatusByQuery.delete(event.query.queryHash)
      return
    }
    if (event.type !== 'updated' || event.action.type !== 'success') return

    const status = readOwnerLicenceStatus(event.query.state.data)
    const hash = event.query.queryHash
    const previous = lastStatusByQuery.get(hash)
    lastStatusByQuery.set(hash, status)

    // A first observation counts as a transition from "unknown": a stage nobody had open may be
    // the one whose owner renewed. Later reads act only on a real change, so a refetch of a
    // writable stage does not replay held groups that belong to other, still-expired projects.
    if (status === null || !WRITABLE_STATUSES.has(status)) return
    // `undefined` (never seen) and `null` (seen without a status) are both "not writable".
    if (!WRITABLE_STATUSES.has(previous ?? '')) {
      void nativeQueueManager.retryLicenceBlocked()
    }
  })
}

export function useLicenceRenewalRetry(queryClient: QueryClient): void {
  useEffect(() => watchOwnerLicenceRenewal(queryClient), [queryClient])
}
